/**
 * Klasse FRIST_CRUD_CLICK_NOT_CHAT — Fristen am sichtbaren Akt pflegen.
 *
 * Polar:
 *   VERBOTEN: Write ohne Mandant, sichtbare Akte-ID und passenden Aktenpfad;
 *   fremde Akte oder localStorage-Erfolg als Ersatz für PB/n8n.
 *   ERLAUBT: create/update über PORTAL_FRIST_APPROVAL für die bereits
 *   PB-/Mitgliedschafts-geprüfte Akte. ARKO bleibt optional.
 *
 * Der Client-Check verhindert Fehlbedienung. Die autoritative Prüfung läuft
 * zusätzlich im n8n-Vertrag gegen portal_akten + portal_akten_members.
 */

import type { PortalFristRecord } from './fristApprovalTypes';

export const PORTAL_FRISTEN_CRUD_CLASS = 'FRIST_CRUD_CLICK_NOT_CHAT' as const;

export const PORTAL_FRISTEN_CRUD_TYPES = [
  {
    id: 'berufung_zpo',
    label: 'Berufung (ZPO)',
    regel_id: 'berufung_zpo_v1',
  },
  {
    id: 'revision_zpo',
    label: 'Revision (ZPO)',
    regel_id: 'revision_zpo_v1',
  },
  {
    id: 'rekurs_avg',
    label: 'Rekurs (AußStrG)',
    regel_id: 'rekurs_avg_v1',
  },
] as const;

export type PortalFristenCrudType = (typeof PORTAL_FRISTEN_CRUD_TYPES)[number];

export type PortalFristWriteScope = {
  customerId?: string | null;
  akteId?: string | null;
  akteNcPath?: string | null;
  visibleAkteId?: string | null;
  visibleAkteNcPath?: string | null;
};

export type PortalFristWriteScopeResult =
  | { ok: true; customerId: string; akteId: string; akteNcPath: string }
  | { ok: false; error: string };

export function normalizePortalFristAktePath(path?: string | null): string {
  const raw = path?.trim() ?? '';
  if (!raw) return '';
  const normalized = raw.replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\/+/, '');
  return `/${normalized.replace(/\/+$/, '')}/`;
}

/** Slash-only Drift darf den Aktenersteller nicht mit 403 aussperren. */
export function portalFristPathsEquivalent(
  left?: string | null,
  right?: string | null,
): boolean {
  const a = normalizePortalFristAktePath(left);
  const b = normalizePortalFristAktePath(right);
  if (!a && !b) return true;
  return Boolean(a) && a === b;
}

function escapePortalFristenPbFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Reload-Filter: S2-Fristen werden primaer ueber die stabile `akte_id` gelesen.
 * Der Pfad bleibt nur Legacy-Fallback fuer alte ARKO-Zeilen ohne `akte_id`.
 */
export function buildPortalFristenPbFilter(
  customerId: string,
  akteNcPath?: string,
  akteId?: string,
): string {
  const cid = escapePortalFristenPbFilterValue(customerId.trim());
  const aid = escapePortalFristenPbFilterValue(akteId?.trim() ?? '');
  const canonicalPath = normalizePortalFristAktePath(akteNcPath);
  const pathVariants = canonicalPath
    ? Array.from(new Set([canonicalPath, canonicalPath.replace(/^\//, '')]))
        .map((path) => `akte_nc_path='${escapePortalFristenPbFilterValue(path)}'`)
        .join(' || ')
    : '';

  if (aid) {
    const legacyPath = pathVariants ? ` || (akte_id='' && (${pathVariants}))` : '';
    return `(customer_id='${cid}' && status='aktiv' && (akte_id='${aid}'${legacyPath}))`;
  }
  if (pathVariants) {
    return `(customer_id='${cid}' && status='aktiv' && (${pathVariants}))`;
  }
  return `(customer_id='${cid}' && status='aktiv')`;
}

/** Client-Pol; n8n wiederholt diese Prüfung serverseitig gegen PocketBase. */
export function validatePortalFristWriteScope(
  scope: PortalFristWriteScope,
): PortalFristWriteScopeResult {
  const customerId = scope.customerId?.trim() ?? '';
  const akteId = scope.akteId?.trim() ?? '';
  const akteNcPath = normalizePortalFristAktePath(scope.akteNcPath);
  const visibleAkteId = scope.visibleAkteId?.trim() ?? '';
  const visibleAkteNcPath = normalizePortalFristAktePath(scope.visibleAkteNcPath);

  if (!customerId) return { ok: false, error: 'Mandant fehlt — Frist wurde nicht gespeichert.' };
  if (!akteId) return { ok: false, error: 'Akte fehlt — Frist wurde nicht gespeichert.' };
  if (!visibleAkteId || visibleAkteId !== akteId) {
    return { ok: false, error: 'Diese Akte ist nicht sichtbar — Schreiben verweigert.' };
  }
  if (
    akteNcPath &&
    visibleAkteNcPath &&
    !portalFristPathsEquivalent(akteNcPath, visibleAkteNcPath)
  ) {
    return { ok: false, error: 'Aktenpfad passt nicht zur sichtbaren Akte.' };
  }
  return { ok: true, customerId, akteId, akteNcPath };
}

export function portalFristRecordBelongsToWriteScope(
  record: PortalFristRecord,
  scope: Extract<PortalFristWriteScopeResult, { ok: true }>,
): boolean {
  if (record.customer_id.trim() !== scope.customerId) return false;
  if (record.akte_id?.trim() && record.akte_id.trim() !== scope.akteId) return false;
  if (!scope.akteNcPath) return record.akte_id?.trim() === scope.akteId;
  if (!record.akte_nc_path?.trim()) return record.akte_id?.trim() === scope.akteId;
  return portalFristPathsEquivalent(record.akte_nc_path, scope.akteNcPath);
}

export function resolvePortalFristenCrudType(
  record?: Pick<PortalFristRecord, 'frist_typ' | 'regel_id'> | null,
): PortalFristenCrudType | null {
  if (!record) return PORTAL_FRISTEN_CRUD_TYPES[0];
  const fristTyp = record.frist_typ.trim().toLowerCase();
  const regelId = record.regel_id.trim().toLowerCase();
  const exact = PORTAL_FRISTEN_CRUD_TYPES.find(
    (option) =>
      option.id === fristTyp ||
      option.label.toLowerCase() === fristTyp ||
      option.regel_id === regelId,
  );
  if (exact) return exact;
  const blob = `${regelId} ${fristTyp}`;
  if (blob.includes('rekurs')) return PORTAL_FRISTEN_CRUD_TYPES.find((row) => row.id === 'rekurs_avg') ?? null;
  if (blob.includes('revision')) return PORTAL_FRISTEN_CRUD_TYPES.find((row) => row.id === 'revision_zpo') ?? null;
  if (blob.includes('berufung')) return PORTAL_FRISTEN_CRUD_TYPES.find((row) => row.id === 'berufung_zpo') ?? null;
  return null;
}

export function buildPortalFristManualCardId(
  action: 'create' | 'update',
  akteId: string,
): string {
  const safeAkteId = akteId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 40) || 'akte';
  return `manual-${action}-${safeAkteId}-${Date.now().toString(36)}`;
}
